import { useState } from "react"
import { Link } from "@inertiajs/react"
import { ArrowLeft, PackageCheck } from "lucide-react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import { PageHeader, SectionHeader } from "@/components/ui/headers"
import { StatusBadge } from "@/components/ui/status-badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { CancelReasonDialog } from "@/features/staff/cancel-reason-dialog"
import type { CancellationReasonOption } from "@/features/staff/cancellations"
import {
  PURCHASING_PATHS,
  formatQuantity,
  purchaseTone,
  tPurchasing,
  type Purchase,
} from "@/features/staff/purchasing/purchasing"
import { formatDateTime, formatMoney } from "@/lib/formatters"
import {
  patchInline,
  toastInlineUpdateError,
  toastInlineUpdateSuccess,
} from "@/lib/inline-update"

export type PurchaseDetailProps = {
  purchase: Purchase
  cancelReasons: CancellationReasonOption[]
  currency: string
  timeZone: string | null
}

function Figure({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <div className="space-y-1">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="text-foreground text-sm font-semibold tabular-nums" data-testid={testId}>
        {value}
      </p>
    </div>
  )
}

/**
 * One delivery, and the two things that can still happen to it.
 *
 * ## Receiving is offered exactly when the model would accept it
 *
 * `receivable` and `cancellable` are computed server-side from
 * `Purchase#receive!` and `#cancel!`'s own predicates, never re-derived here. A
 * screen that decided for itself would drift the first time the model gained a
 * rule.
 *
 * ## A received delivery is not cancellable, and this screen says why
 *
 * Receiving already added every line to the append-only ledger and repointed
 * each supply's weighted average. Undoing that would be an edit to a ledger that
 * corrects with new rows in the opposite direction and never with edits — so a
 * delivery that went back on the truck is a `transfer_out` movement registered
 * in inventory, with its own actor and its own reason. Offering "cancelar" here
 * would promise a rollback that cannot exist, so the button is absent and the
 * sentence that replaces it points at the screen that can actually help.
 *
 * ## The unit rides on every quantity
 *
 * There is no conversion anywhere in this feature: a line's quantity is in the
 * supply's own unit and lands in the ledger untranslated. A bare figure here is
 * how a reader concludes the delivery was 20 sacks when the ledger recorded 20
 * grams.
 */
export function PurchaseDetail({
  purchase,
  cancelReasons,
  currency,
  timeZone,
}: PurchaseDetailProps) {
  const [confirmingReceive, setConfirmingReceive] = useState(false)
  const [receiving, setReceiving] = useState(false)

  const zone = timeZone ?? undefined

  const receive = () => {
    if (receiving) return

    setReceiving(true)
    patchInline(PURCHASING_PATHS.receivePurchase(purchase.id))
      .then((result) => {
        setConfirmingReceive(false)
        toastInlineUpdateSuccess(result)
      })
      .catch((error: unknown) => toastInlineUpdateError(error))
      .finally(() => setReceiving(false))
  }

  return (
    <MainContainer size="md">
      <div className="space-y-6">
        <PageHeader
          title={purchase.supplierName}
          description={
            purchase.invoiceNumber
              ? tPurchasing("row_invoice", { invoice: purchase.invoiceNumber })
              : tPurchasing("row_no_invoice")
          }
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <Button asChild variant="outline" data-testid="purchase-back">
                <Link href={PURCHASING_PATHS.purchases}>
                  <ArrowLeft className="size-4" />
                  {tPurchasing("back_to_purchases")}
                </Link>
              </Button>

              {purchase.cancellable && (
                <CancelReasonDialog
                  path={PURCHASING_PATHS.cancelPurchase(purchase.id)}
                  payload={(reason) => ({ cancel_reason: reason })}
                  reasons={cancelReasons}
                  disabled={receiving}
                  triggerLabel={tPurchasing("purchase_cancel")}
                  triggerVariant="outline"
                  triggerSize="default"
                  triggerTestId={`purchase-cancel-${purchase.id}`}
                />
              )}

              {purchase.receivable && (
                <Button
                  type="button"
                  disabled={receiving}
                  onClick={() => setConfirmingReceive(true)}
                  data-testid={`purchase-receive-${purchase.id}`}
                >
                  <PackageCheck className="size-4" />
                  {tPurchasing("purchase_receive")}
                </Button>
              )}
            </div>
          }
        />

        <Card density="compact">
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge
                tone={purchaseTone(purchase.status)}
                label={purchase.statusLabel}
                data-testid="purchase-status"
              />
              {purchase.status === "received" && (
                <span className="text-muted-foreground text-xs" data-testid="purchase-received-by">
                  {tPurchasing("received_by", {
                    name: purchase.receivedByName ?? tPurchasing("unknown_actor"),
                    at: formatDateTime(purchase.receivedAt, { timeZone: zone }),
                  })}
                </span>
              )}
              {purchase.status === "cancelled" && (
                <span className="text-muted-foreground text-xs" data-testid="purchase-cancelled-by">
                  {tPurchasing("cancelled_by", {
                    name: purchase.cancelledByName ?? tPurchasing("unknown_actor"),
                    at: formatDateTime(purchase.cancelledAt, { timeZone: zone }),
                    reason: purchase.cancelReasonLabel ?? "—",
                  })}
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Figure
                label={tPurchasing("row_lines_label")}
                value={String(purchase.lineCount)}
                testId="purchase-line-count"
              />
              <Figure
                label={tPurchasing("purchase_total_label")}
                value={formatMoney(purchase.totalCost, { currency })}
                testId="purchase-total"
              />
              <Figure
                label={tPurchasing("created_at_label")}
                value={formatDateTime(purchase.createdAt, { timeZone: zone })}
              />
              <Figure
                label={tPurchasing("received_at_label")}
                value={
                  purchase.receivedAt
                    ? formatDateTime(purchase.receivedAt, { timeZone: zone })
                    : "—"
                }
              />
            </div>

            {purchase.status === "received" && (
              <p className="text-muted-foreground text-xs" data-testid="purchase-received-note">
                {tPurchasing("received_note")}
              </p>
            )}
          </CardContent>
        </Card>

        <section>
          <SectionHeader title={tPurchasing("lines_title")} />
          <Card density="compact">
            <CardContent className="overflow-x-auto px-0">
              <Table data-testid="purchase-lines">
                <TableHeader>
                  <TableRow>
                    <TableHead>{tPurchasing("line_supply_label")}</TableHead>
                    <TableHead className="text-right">
                      {tPurchasing("line_quantity_label")}
                    </TableHead>
                    <TableHead className="text-right">
                      {tPurchasing("line_unit_cost_short")}
                    </TableHead>
                    <TableHead className="text-right">{tPurchasing("line_total_label")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(purchase.lines ?? []).map((line) => (
                    <TableRow key={line.id} data-testid={`purchase-line-${line.id}`}>
                      <TableCell className="font-medium">{line.supplyName}</TableCell>
                      <TableCell
                        className="text-right tabular-nums"
                        data-testid={`purchase-line-${line.id}-quantity`}
                      >
                        {formatQuantity(line.quantity, line.unitLabel)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney(line.unitCost, { currency })}
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">
                        {formatMoney(line.lineCost, { currency })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </section>
      </div>

      <AlertDialog
        open={confirmingReceive}
        onOpenChange={(next) => !receiving && setConfirmingReceive(next)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tPurchasing("receive_title")}</AlertDialogTitle>
            <AlertDialogDescription>{tPurchasing("receive_description")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={receiving} data-testid="purchase-receive-cancel">
              {tPurchasing("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={receiving}
              onClick={(event) => {
                // The dialog closes itself on click; receiving is a request that
                // can fail, and a dialog that vanishes before the answer leaves
                // the manager with no idea whether stock moved.
                event.preventDefault()
                receive()
              }}
              data-testid="purchase-receive-confirm"
            >
              {receiving ? tPurchasing("receive_submitting") : tPurchasing("receive_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainContainer>
  )
}
